/**
 * Geometry for the Track Difference view, shared by the on-screen SVG and the
 * MP4 export's Canvas2D renderer.
 *
 * Both consume exactly this: the same projection, the same ribbon heights, the
 * same depth-bucketed draw order. The export is meant to be the thing the user
 * just watched, so anything either side re-derived on its own would be a way for
 * the video to disagree with the panel.
 */
import { heightFullScale, HEIGHT_MODES, liftResponse } from './trackDifference.js';
import { convertValue, getDisplayUnit, getDistanceUnit } from './unitConvert.js';

// The projection fits into this box; both renderers scale it to their own size.
export const VIEW_BOX = { width: 1000, height: 540, fitWidth: 900, fitHeight: 450 };

export const ELEVATION_SCALE = 4;
// Full-height ribbon, as a fraction of the circuit's longest horizontal extent.
// Sizing against the track rather than a pixel count keeps a kart track and a
// road course looking the same.
export const RIBBON_SPAN_FRACTION = 0.105;
// Near/far size ratio of the weak perspective divide. Enough to tell the far
// side of the circuit from the near one at a low camera, not enough to distort.
const PERSPECTIVE = 0.22;
// Painter's-algorithm resolution. Segments sort into buckets and batch by color
// inside one, which is what keeps playback off a DOM node per station.
export const DEPTH_BUCKETS = 8;
export const EVEN_COLOR = '#8b95a5';

/**
 * Ribbon stroke weights, in the device pixels both renderers stroke in.
 *
 * Shared rather than living in the stylesheet because the MP4 renderer draws
 * the same ribbon in Canvas2D and the two would otherwise drift.
 *
 * The top edge is deliberately heavy: each segment is its own subpath, so
 * round caps on neighbours overlap into a continuous tube sitting on the fill
 * below it. That pairing - a solid cap riding a translucent curtain down to the
 * road - is what gives the ribbon its volume.
 */
export const RIBBON_STROKE = {
  road: 20,
  lineB: 5,
  shadow: 28,
  edge: 8,
  ghostEdge: 3,
  clipped: 3.2,
  cursorWall: 2.5,
};

export const DEFAULT_VIEW = { yaw: -18, pitch: 32 };
export const CAMERA_PRESETS = [
  { key: 'plan', label: 'Plan', pitch: 74 },
  { key: 'threeQuarter', label: '3/4', pitch: 48 },
  { key: 'low', label: 'Low', pitch: 24 },
];
export const EXAGGERATIONS = ['auto', 1, 2, 4];

// Zoom is a magnification of the fitted view: 1 shows the whole circuit.
export const ZOOM_LIMITS = { min: 1, max: 8 };
export const FOLLOW_ZOOM = 3;
// Matched to Lap3D's wheel handling so a notch feels the same in both 3D views.
// Negated there because that zoom is a camera distance and this one is a
// magnification: scrolling away must pull out in each.
export const WHEEL_ZOOM_RATE = 0.0012;
// Follow look-ahead. Weighted forward so the corner arrives before the car does.
export const FOLLOW_WINDOW = { behindSec: 0.4, aheadSec: 1.2 };
/**
 * How the playback marker grows with zoom.
 *
 * Sub-linear on purpose: the road and ribbon are scenery and scale with the
 * track, but the marker is a cursor. Growing it linearly makes it swallow the
 * road at follow zoom, and pinning it makes it vanish.
 */
export const markerZoom = zoom => Math.sqrt(Math.max(1, Number(zoom) || 1));

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/**
 * Orthographic yaw/pitch with a weak perspective divide, projecting three
 * layers per station: the ground (elevation reference), the road surface, the
 * ribbon top lifted above it by `lifts[i]` world metres, and the second lap's
 * own racing line on that same surface.
 *
 * Every layer feeds the bounds fit. Leaving the ribbon out of it would clip a
 * large delta off the top of the frame and, worse, move the fit scale whenever
 * the height scale changed.
 */
export function projectTrack(stations, yawDeg, pitchDeg, lifts) {
  if (!stations?.length) return null;
  const yaw = yawDeg * Math.PI / 180;
  const pitch = pitchDeg * Math.PI / 180;
  const cosYaw = Math.cos(yaw);
  const sinYaw = Math.sin(yaw);
  const sinPitch = Math.sin(pitch);
  const cosPitch = Math.cos(pitch);

  // Larger depth sits further from the camera in this projection.
  const depthOf = (x, y) => x * sinYaw + y * cosYaw;
  const depths = stations.map(station => depthOf(station.xM, station.yM));
  const minDepth = Math.min(...depths);
  const depthSpan = Math.max(1e-6, Math.max(...depths) - minDepth);
  const depthAt = index => (depths[index] - minDepth) / depthSpan;
  // Road and ribbon top of one station share a divisor, so the ribbon stays
  // vertical on screen while distance still shrinks it.
  const shrinkOf = depth => 1 / (1 + PERSPECTIVE * ((depth - minDepth) / depthSpan - 0.5));

  const roadZ = stations.map(station => (station.zM || 0) * ELEVATION_SCALE);
  const at = (x, y, depth, z) => ({
    x: x * cosYaw - y * sinYaw,
    y: -depth * sinPitch - z * cosPitch,
  });
  const own = (index, z) => at(stations[index].xM, stations[index].yM, depths[index], z);
  // B's own line, on the same road surface: both laps are at the same lap
  // fraction, so they share an elevation. Falls back to A where a model predates
  // the second projection, which keeps an old cached compare from throwing.
  const bx = stations.map(station => (station.xBM ?? station.xM));
  const by = stations.map(station => (station.yBM ?? station.yM));
  const depthsB = stations.map((_, index) => depthOf(bx[index], by[index]));

  const layers = {
    ground: stations.map((_, index) => own(index, 0)),
    road: stations.map((_, index) => own(index, roadZ[index])),
    top: stations.map((_, index) => own(index, roadZ[index] + (lifts?.[index] || 0))),
    lineB: stations.map((_, index) => at(bx[index], by[index], depthsB[index], roadZ[index])),
  };
  const layerDepths = {
    ground: depths, road: depths, top: depths, lineB: depthsB,
  };

  const all = [layers.ground, layers.road, layers.top].flat();
  const cx = all.reduce((sum, point) => sum + point.x, 0) / all.length;
  const cy = all.reduce((sum, point) => sum + point.y, 0) / all.length;
  const divided = {};
  for (const key of Object.keys(layers)) {
    divided[key] = layers[key].map((point, index) => {
      const shrink = shrinkOf(layerDepths[key][index]);
      return { x: cx + (point.x - cx) * shrink, y: cy + (point.y - cy) * shrink };
    });
  }

  const bounds = [divided.ground, divided.road, divided.top, divided.lineB].flat();
  const minX = Math.min(...bounds.map(point => point.x));
  const maxX = Math.max(...bounds.map(point => point.x));
  const minY = Math.min(...bounds.map(point => point.y));
  const maxY = Math.max(...bounds.map(point => point.y));
  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);
  const scale = Math.min(VIEW_BOX.fitWidth / width, VIEW_BOX.fitHeight / height);
  const offsetX = (VIEW_BOX.width - width * scale) / 2;
  const offsetY = (VIEW_BOX.height - height * scale) / 2;
  const fit = point => ({
    x: offsetX + (point.x - minX) * scale,
    y: offsetY + (point.y - minY) * scale,
  });

  return {
    road: divided.road.map(fit),
    ground: divided.ground.map(fit),
    top: divided.top.map(fit),
    lineB: divided.lineB.map(fit),
    // Farthest bucket first, so a later bucket always paints over an earlier one.
    bucketOf: index => clamp(Math.floor((1 - depthAt(index)) * DEPTH_BUCKETS), 0, DEPTH_BUCKETS - 1),
    pxPerLiftM: cosPitch * scale,
  };
}

/**
 * Per-station ribbon lift (world metres) and per-segment color.
 *
 * Color follows whichever metric is driving the height, so the two can never
 * disagree — a driver could otherwise read as tall-and-teal while actually
 * losing time, if height said "speed" and color said "time gained".
 */
export function buildRibbon(model, { heightMode, exaggeration, colorA, colorB }) {
  const spec = HEIGHT_MODES[heightMode] || HEIGHT_MODES.gap;
  const fullScale = heightFullScale(model, heightMode, exaggeration);
  const maxLiftM = (model?.spanM || 1) * RIBBON_SPAN_FRACTION;
  const values = model.stations.map(station => Number(station[spec.field]) || 0);
  const lifts = values.map(value => Math.abs(liftResponse(value / fullScale)) * maxLiftM);
  const segments = [];
  for (let index = 0; index < values.length - 1; index += 1) {
    const value = values[index + 1];
    segments.push({
      index,
      color: value > spec.evenEps ? colorA : value < -spec.evenEps ? colorB : EVEN_COLOR,
      clipped: Math.abs(value) > fullScale,
    });
  }
  return { lifts, segments, fullScale, maxLiftM, spec };
}

/**
 * Sort the ribbon into depth buckets, grouped by color inside each.
 *
 * Returns segment *indices* rather than drawing commands, so the SVG side can
 * batch them into path strings and the canvas side can stroke them directly
 * without either re-deciding the draw order.
 */
export function bucketRibbon(projection, segments, activeThrough) {
  const buckets = Array.from({ length: DEPTH_BUCKETS }, () => ({
    road: [], active: new Map(), ghost: new Map(),
  }));
  const clipped = [];
  const push = (map, color, index) => {
    const list = map.get(color);
    if (list) list.push(index); else map.set(color, [index]);
  };
  for (const segment of segments) {
    const bucket = buckets[projection.bucketOf(segment.index)];
    bucket.road.push(segment.index);
    if (segment.index < activeThrough) {
      push(bucket.active, segment.color, segment.index);
      if (segment.clipped) clipped.push(segment.index);
    } else {
      push(bucket.ghost, segment.color, segment.index);
    }
  }
  return { buckets, clipped };
}

const finite = value => Number.isFinite(Number(value));

/**
 * Display units for the gain rate, which is stored per kilometre.
 *
 * Shown over a corner-sized span rather than per mile: measured gain rates run
 * about 1.3 s/km, which is 0.04 s per 100 ft — too small for two decimals to
 * resolve — and 2.1 s per mile, a distance nobody has ever had to judge a tenth
 * over. 500 ft puts the number in the 0.05-0.50 range, across a stretch of road
 * a driver can actually picture.
 */
export function gainUnits(unitPrefs) {
  return unitPrefs?.speed === 'km/h'
    ? { factor: 0.15, unit: 's/150m' }       // s per km -> s per 150 m
    : { factor: 0.1524, unit: 's/500ft' };   // s per km -> s per 500 ft
}

/** What full ribbon height is worth, in the user's units. */
export function scaleLabel(fullScale, heightMode, unitPrefs) {
  if (heightMode === 'speed') {
    return `${Math.round(convertValue(fullScale, 'mph', unitPrefs))} ${getDisplayUnit('mph', unitPrefs)}`;
  }
  if (heightMode === 'gap') return `${fullScale.toFixed(2)} s`;
  if (heightMode === 'line') {
    const line = lineUnits(unitPrefs);
    return `${(fullScale * line.factor).toFixed(1)} ${line.unit}`;
  }
  const gain = gainUnits(unitPrefs);
  return `${(fullScale * gain.factor).toFixed(2)} ${gain.unit}`;
}

const signed = (value, digits) => (
  `${value > 0 ? '+' : value < 0 ? '\u2212' : ''}${Math.abs(value).toFixed(digits)}`
);

/**
 * The centre readout: the *instantaneous* quantity, whichever mode is showing.
 *
 * It deliberately pairs with the running-gap pill rather than repeating it —
 * the pill carries the cumulative standing, this carries what is happening right
 * here, so both are on screen at once in every mode. What must never disagree is
 * ribbon height and ribbon color, and those are decided together in
 * `buildRibbon`.
 */
export function headlineFor(station, { heightMode, unitPrefs, lapA, lapB }) {
  if (heightMode === 'speed') {
    const a = finite(station?.speedA) ? convertValue(station.speedA, 'mph', unitPrefs) : null;
    const b = finite(station?.speedB) ? convertValue(station.speedB, 'mph', unitPrefs) : null;
    const value = a != null && b != null ? a - b : null;
    return {
      label: 'SPEED DIFFERENCE',
      unit: getDisplayUnit('mph', unitPrefs),
      text: value == null ? '\u2014' : `${value > 0 ? '+' : ''}${Math.round(value)}`,
      leader: value == null || Math.abs(value) < 0.5 ? 'even' : value > 0 ? lapA.name : lapB.name,
    };
  }
  if (heightMode === 'line') {
    // In Line mode the separation *is* the instantaneous quantity, so it is what
    // belongs here; the pill still carries the cumulative gap.
    const line = lineUnits(unitPrefs);
    const offset = finite(station?.lineOffsetM) ? station.lineOffsetM * line.factor : null;
    const apart = offset == null ? null : Math.abs(offset);
    return {
      label: 'LINES APART',
      unit: line.unit,
      text: apart == null ? '\u2014' : apart.toFixed(1),
      leader: apart == null || apart < 0.15 * line.factor ? 'same line'
        : `${offset > 0 ? lapA.name : lapB.name} left`,
    };
  }
  const gain = gainUnits(unitPrefs);
  const value = finite(station?.gainPerKmS) ? station.gainPerKmS * gain.factor : null;
  const even = value == null || Math.abs(value) < 0.005;
  return {
    label: 'GAINING HERE',
    unit: gain.unit,
    text: value == null ? '\u2014' : signed(value, 2),
    leader: even ? 'even' : value > 0 ? lapA.name : lapB.name,
  };
}

/** Running gap pill: who is ahead at this station, and by how much. */
export function gapLabel(station, lapA, lapB) {
  if (!station || Math.abs(station.gapSec) < 0.005) return { lead: 'EVEN', value: '0.000 s' };
  const leader = station.gapSec > 0 ? lapA : lapB;
  return { lead: `${leader.name} AHEAD`, value: `${Math.abs(station.gapSec).toFixed(3)} s` };
}

/**
 * The visible sub-rectangle of the fitted view, in VIEW_BOX units.
 *
 * Zoom and pan are a transform *on top of* `projectTrack`, never an input to
 * it: the projection stays the one auto-fit both renderers share, so a zoomed
 * panel and a zoomed export cannot disagree about where anything is. Clamped to
 * the box so panning cannot wander off into empty space.
 */
export function viewportRect({ zoom = 1, centerX, centerY } = {}) {
  const z = clamp(Number(zoom) || 1, ZOOM_LIMITS.min, ZOOM_LIMITS.max);
  const width = VIEW_BOX.width / z;
  const height = VIEW_BOX.height / z;
  const cx = Number.isFinite(centerX) ? centerX : VIEW_BOX.width / 2;
  const cy = Number.isFinite(centerY) ? centerY : VIEW_BOX.height / 2;
  return {
    zoom: z,
    width,
    height,
    x: clamp(cx - width / 2, 0, VIEW_BOX.width - width),
    y: clamp(cy - height / 2, 0, VIEW_BOX.height - height),
  };
}

/**
 * Where a following camera looks at `elapsed` seconds.
 *
 * Averaging the road over a time window rather than lerping toward the marker
 * each frame is what makes this deterministic: scrubbing, live playback and the
 * frame-stepped MP4 export all step time differently, and a per-frame lerp would
 * hand each of them a different camera path for the same lap.
 */
export function followCenter(projection, stations, elapsed, window = FOLLOW_WINDOW) {
  if (!projection?.road?.length || !stations?.length) return null;
  const { behindSec, aheadSec } = window;
  // Triangular weights rather than a flat average over the window. A flat one
  // only changes when a station crosses an edge, so the camera holds still and
  // then pops; fading each station in and out makes the mean move continuously
  // with `elapsed`, which is what stops the pan stepping.
  const centre = (aheadSec - behindSec) / 2;
  const halfWidth = (aheadSec + behindSec) / 2;
  let sumX = 0;
  let sumY = 0;
  let total = 0;
  for (let index = 0; index < stations.length; index += 1) {
    const offset = stations[index].clockSec - elapsed;
    const weight = 1 - Math.abs(offset - centre) / halfWidth;
    if (weight <= 0) continue;
    sumX += projection.road[index].x * weight;
    sumY += projection.road[index].y * weight;
    total += weight;
  }
  if (!total) return null;
  return { x: sumX / total, y: sumY / total };
}

/** Centre of the projected track, i.e. where an unpanned view looks. */
export function projectionCenter(projection) {
  const points = [...projection.road, ...projection.ground, ...projection.top];
  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

/**
 * Fractional station index at `elapsed`, for drawing between stations.
 *
 * A lap is ~420 stations, so they sit about 0.2 s apart while a frame is 0.017 s.
 * Snapping to the nearest station therefore holds the marker still for a dozen
 * frames and then jumps it — measured at 78% of frames motionless — which is
 * exactly what playback jerkiness looks like.
 */
export function fractionalStationAt(stations, elapsed) {
  const last = stations.length - 1;
  if (!(last > 0) || elapsed <= stations[0].clockSec) return 0;
  if (elapsed >= stations[last].clockSec) return last;
  let lo = 0;
  let hi = last;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (stations[mid].clockSec <= elapsed) lo = mid; else hi = mid;
  }
  const span = stations[hi].clockSec - stations[lo].clockSec;
  return span > 0 ? lo + (elapsed - stations[lo].clockSec) / span : lo;
}

/** Point `frac` of the way from `points[whole]` to the next one. */
export function lerpPoint(points, whole, frac) {
  const a = points[Math.max(0, Math.min(points.length - 1, whole))];
  const b = points[Math.max(0, Math.min(points.length - 1, whole + 1))];
  return { x: a.x + (b.x - a.x) * frac, y: a.y + (b.y - a.y) * frac };
}

/**
 * Sector state at a point in the lap: what has been driven, what is being
 * driven, and what is still to come.
 *
 * Shared so the exported clip labels the circuit the same way the panel does —
 * "Back Straight", "Loop", "Old Ram" are the names a driver debriefs in, and a
 * video with no scrubber needs them more than the panel does.
 */
export function sectorStates(sectors, fraction) {
  const at = Number.isFinite(fraction) ? fraction : 0;
  return (sectors || []).map((sector, index) => {
    const span = Math.max(1e-6, sector.endFrac - sector.startFrac);
    const done = at >= sector.endFrac - 0.0005;
    const active = !done && at >= sector.startFrac;
    return {
      ...sector,
      label: sector.label || `Sector ${index + 1}`,
      state: done ? 'done' : active ? 'active' : 'upcoming',
      progress: done ? 1 : active ? Math.min(1, (at - sector.startFrac) / span) : 0,
    };
  });
}

/**
 * How one sector's result reads. `text` is the panel's phrasing; `compact` is
 * the signed form for a video cell too narrow to carry a name, where the
 * winner's color is what says who.
 */
export function sectorReadout(sector, lapA, lapB) {
  if (!sector || Math.abs(sector.gapSec) < 0.005) {
    return { even: true, color: EVEN_COLOR, text: 'Even', compact: 'EVEN' };
  }
  const aAhead = sector.gapSec > 0;
  const magnitude = Math.abs(sector.gapSec).toFixed(3);
  return {
    even: false,
    color: aAhead ? lapA.accent : lapB.accent,
    text: `${aAhead ? lapA.name : lapB.name} ${magnitude}s`,
    compact: `${aAhead ? '+' : '\u2212'}${magnitude}`,
  };
}

/** Line separation is stored in metres; imperial users read it in feet. */
export function lineUnits(unitPrefs) {
  return unitPrefs?.speed === 'km/h'
    ? { factor: 1, unit: 'm' }
    : { factor: 3.28084, unit: getDistanceUnit(unitPrefs) };
}
